import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import SkillCheckHero from "@/components/SkillCheckHero";
import ExampleCard from "@/components/ExampleCard";
import { ImpactStoriesRail, ImpactStoriesCarousel } from "@/components/ImpactStories";
import { AmericaPromoCard } from "@/lib/america250";
import { motion, AnimatePresence } from "framer-motion";
import { capture } from "@/lib/analytics";
import { Target, Award, Flame, Sparkles, Zap, Brain, TrendingUp, Lock,
  Briefcase, Hammer, Palette, Rocket, Settings, Compass,
  Workflow, Terminal, ChevronDown } from "lucide-react";
import { BELTS } from "@/components/BeltSystem";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

/**
 * Landing — single-purpose page that drives one action: take the AI Skill
 * Check. After the hero, the page brings back the Prompt Dojo training
 * identity through three sections (Enter the Dojo → Build Skills You'll
 * Actually Use → FAQ). The Skill Check now creates the journey after the
 * user takes action.
 */
export default function Landing() {
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);

  const startSkillCheck = (surface) => {
    capture("skill_check_cta_clicked", { surface });
    navigate("/skill-check");
  };

  // Smooth-scroll helper for the Dojo journey tiles. Tiles 2 + 3 deep-link
  // to sections further down the same page so users can preview what comes
  // after the Skill Check without leaving the homepage.
  const scrollToSection = (id, surface) => {
    capture("landing_journey_tile_clicked", { surface });
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    capture("homepage_visit");
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      try { localStorage.setItem("pending_referral_code", ref.trim().toUpperCase()); }
      catch (e) { console.warn("Could not persist referral code:", e); }
    }
  }, []);

  return (
    <div className="App">
      <Header />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode="signup" />

      {/* ─────────── SKILL CHECK HERO + EXAMPLE + IMPACT STORIES ─────────── */}
      <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-[minmax(0,1fr)_290px] lg:gap-8 lg:items-start lg:px-8">
        <div className="min-w-0">
          <SkillCheckHero
            onPrimary={() => startSkillCheck("landing_hero_primary")}
            onSecondary={() => startSkillCheck("landing_hero_secondary")}
          />

          {/* See-the-Difference example card (persona-report REC-c8311e735f2c) */}
          <ExampleCard />
        </div>

        {/* Desktop side rail — Real-World AI Wins */}
        <div className="hidden lg:block pt-12">
          <ImpactStoriesRail />
        </div>
      </div>

      {/* Mobile — compact swipeable impact stories below the example card */}
      <ImpactStoriesCarousel />

      {/* ─────────── America 250 promo card (active only during the campaign window) ─────────── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-2" data-testid="landing-am250-section">
        <AmericaPromoCard />
      </section>

      {/* ─────────── 2. ENTER THE DOJO ─────────── */}
      <section
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32"
        data-testid="landing-dojo-section"
      >
        <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05] font-semibold">
            Enter the Dojo
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--ink-2)] leading-relaxed">
            Prompt Dojo uses your skill results to build your training path to
            sharpen your AI skills one challenge at a time.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-3 gap-6 mt-12 sm:mt-14">
          {/* TILE 1 — Find Your Level (primary CTA — Start Skill Check) */}
          <DojoJourneyTile
            n={1}
            icon={Target}
            title="Find Your Level"
            desc="Take the Skill Check and discover your AI starting point."
            action="Start Skill Check"
            accent="#10B981"
            primary
            onClick={() => startSkillCheck("landing_dojo_tile_1")}
            testid="landing-dojo-tile-1"
            track="start-skill-check"
            delay={0}
          />

          {/* TILE 2 — Choose Your Identity (scrolls to identity section) */}
          <DojoJourneyTile
            n={2}
            icon={Award}
            title="Choose Your Identity"
            desc="Explore the AI identities and see how your training path can match your goals."
            action="Explore Identities"
            accent="#7C3AED"
            onClick={() => scrollToSection("identity", "landing_dojo_tile_2")}
            testid="landing-dojo-tile-2"
            delay={0.08}
          />

          {/* TILE 3 — Begin Your Training (scrolls to belt progression) */}
          <DojoJourneyTile
            n={3}
            icon={Flame}
            title="Begin Your Training"
            desc="See how challenges and belts help you sharpen your AI skills over time."
            action="View Belt Journey"
            accent="#F97316"
            onClick={() => scrollToSection("belt-journey", "landing_dojo_tile_3")}
            testid="landing-dojo-tile-3"
            delay={0.16}
          />
        </div>
      </section>

      {/* ─────────── 3. CHOOSE YOUR AI IDENTITY ─────────── */}
      <IdentitySection onStart={() => startSkillCheck("landing_identity")} />

      {/* ─────────── 4. PROGRESS THROUGH THE DOJO BELT SYSTEM ─────────── */}
      <BeltProgressionSection />

      {/* ─────────── 5. WHAT BETTER AI SKILLS CAN HELP YOU DO ─────────── */}
      <BetterAISkillsSection />

      {/* ─────────── 6. EXAMPLE OUTCOMES / TESTIMONIALS ─────────── */}
      <ExampleOutcomesSection />

      {/* ─────────── 7. BUILD SKILLS YOU'LL ACTUALLY USE ─────────── */}
      <section
        className="bg-[var(--surface)] border-y border-[var(--line)]"
        data-testid="landing-benefits-section"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05] font-semibold">
              Build Skills You&apos;ll Actually Use
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-5 mt-12">
            {[
              {
                icon: Zap,
                title: "Work Faster",
                desc: "Use AI to save time on writing, planning, research, and everyday tasks.",
                tone: "var(--primary, #2563EB)",
              },
              {
                icon: Brain,
                title: "Think Clearer",
                desc: "Learn how to give AI better context so the answers are more useful.",
                tone: "var(--secondary, #6D28D9)",
              },
              {
                icon: TrendingUp,
                title: "Get Better Results",
                desc: "Sharpen your prompts so AI gives you stronger, more practical outputs.",
                tone: "var(--emerald, #10B981)",
              },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                className="bg-white p-6 rounded-2xl flex items-start gap-4"
                style={{ border: "1px solid #E5E7EB", boxShadow: "0 1px 2px rgba(15,20,25,0.04)" }}
                whileHover={{ y: -2, boxShadow: "0 8px 20px -12px rgba(15,20,25,0.18)", transition: { duration: 0.2 } }}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                data-testid={`landing-benefit-${i}`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${b.tone}12`, color: b.tone, border: `1px solid ${b.tone}22` }}
                >
                  <b.icon size={20} strokeWidth={2.25} />
                </div>
                <div>
                  <h3 className="font-display text-lg leading-tight tracking-tight mb-1.5">
                    {b.title}
                  </h3>
                  <p className="text-sm text-[var(--ink-2)] leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FAQ (intentionally muted — addresses objections without selling) ─────────── */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28" data-testid="landing-faq">
        <motion.h2
          className="font-display text-2xl sm:text-3xl tracking-tight text-[var(--ink)]"
          {...fadeUp}
        >
          FAQ
        </motion.h2>
        <div className="mt-8">
          {[
            { q: "How does the Skill Check work?", a: "You'll answer 3 quick prompt-evaluation questions and rewrite one short prompt. Claude grades the rewrite across six categories — clarity, context, role, goal, output format, and constraints — and we return your Prompt Score, skill level, and a recommended training path." },
            { q: "Who grades my prompt?", a: "Anthropic's Claude Sonnet 4.5 scores your rewrite across six categories. You see the breakdown plus a written explanation." },
            { q: "What do I get after the Skill Check?", a: "A 0–100 Prompt Score, your skill / belt level, the two areas you should improve first, and a recommended training path mapped to real lessons inside Prompt Dojo." },
            { q: "How long does it take?", a: "Three multiple-choice questions and one short rewrite. Most people finish well under three minutes." },
            { q: "What happens after I see my score?", a: "You pick: \"Choose for Me\" (start the recommended path right away) or \"Build My Own Path\" (hand-pick your focus badges in onboarding). Either way, your score and training direction are saved to your account." },
          ].map((f, i) => (
            <motion.details
              key={f.q}
              className="group border-b border-[var(--line)] py-5 last:border-0"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.04 }}
              data-testid={`landing-faq-${i}`}
            >
              <summary className="cursor-pointer font-display text-base sm:text-lg text-[var(--ink)] list-none flex items-center justify-between gap-3 py-1">
                <span>{f.q}</span>
                <ChevronDown size={16} className="text-[var(--ink-3)] group-open:rotate-180 transition-transform flex-shrink-0" />
              </summary>
              <div className="text-[var(--ink-2)] mt-3 leading-relaxed text-sm sm:text-base">{f.a}</div>
            </motion.details>
          ))}
        </div>
      </section>

      {/* ─────────── FINAL CTA (drives Skill Check, not signup) ─────────── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-32">
        <motion.div
          className="rounded-[2.5rem] p-10 sm:p-16 md:p-20 text-center relative overflow-hidden"
          style={{ background: "var(--ink)", color: "#fff" }}
          {...fadeUp}
        >
          <div
            className="absolute -top-24 right-0 w-80 h-80 rounded-full pointer-events-none"
            style={{ background: "rgba(37, 99, 235, 0.2)", filter: "blur(120px)" }}
          />
          <div
            className="absolute -bottom-24 left-0 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: "rgba(16, 185, 129, 0.2)", filter: "blur(120px)" }}
          />
          <div className="relative flex flex-col items-center">
            <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-[-0.022em] max-w-2xl font-semibold">
              Find out where you really stand.
            </h3>
            <p className="mt-4 text-white/75 max-w-xl">
              Honest score. Personalized training path. Sharpen your AI skills one challenge at a time.
            </p>
            <button
              onClick={() => startSkillCheck("landing_final_cta")}
              className="btn-duo whitespace-nowrap mt-8"
              data-testid="landing-final-cta"
              data-track="start-skill-check"
            >
              Start My Skill Check <Sparkles size={14} />
            </button>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}

// ============================================================================
// SECTION COMPONENTS
// Defined outside <Landing/> so React doesn't remount them on every parent
// render. All section copy is centralized here, not pulled from a CMS — the
// product spec is the source of truth.
// ============================================================================

const _fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

// ----------------------------------------------------------------------------
// 3. Choose Your AI Identity — 6 preview badge cards. Identity selection
// itself unlocks after the Skill Check; this section is purely aspirational.
// ----------------------------------------------------------------------------
const IDENTITIES = [
  { icon: Briefcase, title: "Professional", tone: "#2563EB", desc: "Use AI to write faster, organize ideas, summarize information, and improve everyday work." },
  { icon: Hammer,    title: "Builder",      tone: "#F97316", desc: "Use AI to plan, code, prototype, troubleshoot, and bring app or business ideas to life." },
  { icon: Palette,   title: "Creator",      tone: "#7C3AED", desc: "Use AI to create content, brainstorm campaigns, write scripts, and sharpen your message." },
  { icon: Rocket,    title: "Entrepreneur", tone: "#10B981", desc: "Use AI to research opportunities, automate tasks, improve offers, and move faster in business." },
  { icon: Settings,  title: "Operator",     tone: "#0891B2", desc: "Use AI to create systems, SOPs, workflows, automations, and repeatable processes." },
  { icon: Compass,   title: "Custom Path",  tone: "#0F1419", desc: "Mix badges and challenges around your own goals." },
];

function IdentitySection() {
  return (
    <section
      id="identity"
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 scroll-mt-24"
      data-testid="landing-identity-section"
    >
      <motion.div {..._fade} className="max-w-2xl mx-auto text-center">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05] font-semibold">
          Choose the AI Identity That Fits Your Goals
        </h2>
        <p className="mt-4 text-base sm:text-lg text-[var(--ink-2)] leading-relaxed">
          After your Skill Check, Prompt Dojo recommends a path or lets you build
          your own around the way you want to use AI.
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {IDENTITIES.map((id, i) => (
          <motion.div
            key={id.title}
            className="bg-white p-8 rounded-2xl group flex flex-col"
            style={{ border: "1px solid #E5E7EB" }}
            whileHover={{ y: -3, borderColor: id.tone, transition: { duration: 0.2 } }}
            {..._fade}
            transition={{ ..._fade.transition, delay: i * 0.05 }}
            data-testid={`landing-identity-${id.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center mb-6"
              style={{ background: "#FAFAFA", border: "1px solid var(--line)", color: id.tone }}
            >
              <id.icon size={20} strokeWidth={2.25} />
            </div>
            <h3 className="font-display text-lg leading-tight tracking-tight mb-2">{id.title}</h3>
            <p className="text-sm text-[var(--ink-2)] leading-relaxed">{id.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <div
          className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-medium"
          style={{ background: "var(--ink)", color: "#fff", boxShadow: "0 1px 2px rgba(15,20,25,0.15)" }}
        >
          <Lock size={13} strokeWidth={2.5} />
          Identity selection unlocks after your Skill Check
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// 4. Progress Through the Dojo Belt System — 8 expandable belt cards.
// Click expands inline (no modal, no navigation) so the user never leaves
// the homepage before taking the Skill Check.
// ----------------------------------------------------------------------------
const BELT_COPY = {
  white:  { skill: "Find your starting point",       outcome: "Understand what makes a prompt work.",                                       challenges: "0–3 challenges",  developed: "Recognize the gap between vague and effective prompts. Learn the six elements every strong prompt needs." },
  yellow: { skill: "Build better prompts",            outcome: "Give AI clearer goals and context.",                                          challenges: "3–8 challenges",  developed: "Add context, audience, and explicit goals. Get answers you can actually use instead of generic ones." },
  orange: { skill: "Apply AI to real tasks",          outcome: "Use AI for writing, planning, research, or everyday work.",                   challenges: "8–15 challenges", developed: "Translate prompting into daily wins — drafts, summaries, research briefs, and decision support." },
  green:  { skill: "Create repeatable workflows",     outcome: "Turn common tasks into reusable AI-assisted processes.",                      challenges: "15–25 challenges", developed: "Design prompt templates and short chains that turn one-offs into systems." },
  blue:   { skill: "Sharpen your identity path",      outcome: "Train around your role: Professional, Builder, Creator, Entrepreneur, or Operator.", challenges: "25–35 challenges", developed: "Specialize. Build a personal library of role-specific prompts that compound week over week." },
  purple: { skill: "Build with AI",                   outcome: "Use AI to plan, create, automate, or prototype more effectively.",            challenges: "35–50 challenges", developed: "Move from single prompts to multi-step builds. Pair AI with tools you already use." },
  brown:  { skill: "Optimize and refine",             outcome: "Improve outputs, systems, and workflows with stronger prompt strategy.",      challenges: "50–70 challenges", developed: "Spot weak prompts instantly. Iterate fast. Make every output measurably better than the last." },
  black:  { skill: "AI Mastery",                      outcome: "Use AI as a practical advantage in work, business, or building.",             challenges: "70+ challenges",  developed: "AI is part of how you think and execute. You teach others and set the bar for what good prompting looks like." },
};

const BELT_CHIP = {
  white:  { bg: "#F9FAFB", fg: "#111827" },
  yellow: { bg: "#FBBF24", fg: "#78350F" },
  orange: { bg: "#F97316", fg: "#FFEDD5" },
  green:  { bg: "#10B981", fg: "#064E3B" },
  blue:   { bg: "#3B82F6", fg: "#EFF6FF" },
  purple: { bg: "#8B5CF6", fg: "#F5F3FF" },
  brown:  { bg: "#92400E", fg: "#FEF3C7" },
  black:  { bg: "#111827", fg: "#FFFFFF", border: "1px solid #374151" },
};

function BeltProgressionSection() {
  const [openId, setOpenId] = React.useState(null);
  return (
    <section
      id="belt-journey"
      className="bg-[var(--ink)] text-[#FAFAFA] relative overflow-hidden scroll-mt-24"
      data-testid="landing-belt-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <motion.div {..._fade} className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05] font-semibold">
            Progress Through the Dojo Belt System
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(250,250,250,0.7)" }}>
            Start with your Skill Check, then complete daily challenges to sharpen
            your AI skills and move through each belt level.
          </p>
        </motion.div>

        <div className="mt-12 sm:mt-14 max-w-3xl mx-auto">
          {BELTS.map((b, i) => {
            const copy = BELT_COPY[b.id];
            const isOpen = openId === b.id;
            const chip = BELT_CHIP[b.id];
            return (
              <motion.div
                key={b.id}
                className="rounded-2xl mb-3 overflow-hidden"
                style={{
                  background: "#1A2027",
                  border: `1px solid ${isOpen ? b.color : "#2D3748"}`,
                }}
                {..._fade}
                transition={{ ..._fade.transition, delay: i * 0.04 }}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : b.id)}
                  className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-inset"
                  data-testid={`landing-belt-${b.id}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-start sm:items-center gap-2.5 sm:gap-4 min-w-0 flex-col sm:flex-row">
                    <span
                      className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] shrink-0"
                      style={{ background: chip.bg, color: chip.fg, border: chip.border || "none" }}
                    >
                      {b.name}
                    </span>
                    <div className="min-w-0">
                      <div className="font-display text-base sm:text-lg leading-tight tracking-tight">
                        {copy.skill}
                      </div>
                      <div className="text-xs sm:text-sm mt-1 leading-relaxed" style={{ color: "rgba(250,250,250,0.6)" }}>
                        {copy.outcome}
                      </div>
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className="shrink-0 transition-transform duration-300"
                    style={{ color: "rgba(250,250,250,0.5)", transform: isOpen ? "rotate(180deg)" : "none" }}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      style={{ overflow: "hidden" }}
                      data-testid={`landing-belt-${b.id}-detail`}
                    >
                      <div className="px-5 sm:px-6 pb-6 pt-4 border-t border-[#2D3748] grid sm:grid-cols-[170px_1fr] gap-4">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-1" style={{ color: "rgba(250,250,250,0.5)" }}>
                            Challenge range
                          </div>
                          <div className="text-sm" style={{ color: "rgba(250,250,250,0.85)" }}>{copy.challenges}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-1" style={{ color: "rgba(250,250,250,0.5)" }}>
                            Skills developed
                          </div>
                          <div className="text-sm leading-relaxed" style={{ color: "rgba(250,250,250,0.85)" }}>{copy.developed}</div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <p className="text-xs text-center mt-8 leading-relaxed max-w-xl mx-auto" style={{ color: "rgba(250,250,250,0.55)" }}>
          Progress depends on consistency, but daily challenges are designed to
          create noticeable improvement within your first 30 days.
        </p>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// 5. Develop AI Skills That Can Help Unlock Infinite Productivity —
// practical proof. Three skill outcomes, scannable cards. No
// income/automation/app guarantees.
// ----------------------------------------------------------------------------
const SKILL_OUTCOMES = [
  {
    icon: TrendingUp, tone: "#10B981", title: "Make Money",
    desc: "Use AI to sharpen offers, write better outreach, research opportunities, and move faster on business ideas.",
    example: "Improve an offer and draft outreach.",
  },
  {
    icon: Workflow, tone: "#2563EB", title: "Automate Tasks",
    desc: "Turn repetitive work into checklists, SOPs, workflows, and AI-assisted systems.",
    example: "Turn customer requests into a repeatable workflow.",
  },
  {
    icon: Terminal, tone: "#7C3AED", title: "Code and Build",
    desc: "Use AI to plan features, write better dev prompts, troubleshoot issues, and prototype ideas.",
    example: "Map an app idea into clear feature specs.",
  },
];

function BetterAISkillsSection() {
  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32"
      data-testid="landing-better-skills-section"
    >
      <motion.div {..._fade} className="max-w-2xl mx-auto text-center">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05] font-semibold">
          Develop AI Skills That Can Help Unlock Infinite Productivity
        </h2>
        <p className="mt-4 text-base sm:text-lg text-[var(--ink-2)] leading-relaxed">
          Prompt Dojo is built around practical developing skills that can turn
          AI into an unlimited resource.
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-4 mt-12">
        {SKILL_OUTCOMES.map((s, i) => (
          <motion.div
            key={s.title}
            className="relative overflow-hidden rounded-2xl p-5 bg-white group transition-all duration-200"
            style={{
              border: `1px solid var(--line)`,
              boxShadow: "0 1px 0 0 rgba(15,20,25,0.04)",
            }}
            whileHover={{
              y: -3,
              boxShadow: `0 1px 0 0 ${s.tone}33, 0 14px 30px -16px ${s.tone}55`,
              borderColor: `${s.tone}66`,
              transition: { duration: 0.2 },
            }}
            {..._fade}
            transition={{ ..._fade.transition, delay: i * 0.08 }}
            data-testid={`landing-skill-outcome-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {/* Accent wash */}
            <div
              className="absolute inset-0 pointer-events-none opacity-60"
              style={{
                background: `radial-gradient(110% 50% at 100% 0%, ${s.tone}10 0%, transparent 60%)`,
              }}
            />
            <div className="relative">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{
                  background: `linear-gradient(135deg, ${s.tone}1F 0%, ${s.tone}0A 100%)`,
                  color: s.tone,
                  border: `1px solid ${s.tone}26`,
                }}
              >
                <s.icon size={22} strokeWidth={2.25} />
              </div>
              <h3 className="font-display text-xl leading-tight tracking-tight mb-2">
                {s.title}
              </h3>
              <p className="text-sm text-[var(--ink-2)] leading-snug mb-3">
                {s.desc}
              </p>
              <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--ink-3)] flex items-center gap-1.5">
                <span style={{ color: s.tone }}>•</span>
                Example: {s.example}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// 6. Example Outcomes — labeled "Example outcome" so we never imply these
// are verified customer testimonials. Once real testimonials exist they can
// be swapped in via the admin/CMS without touching this component.
// ----------------------------------------------------------------------------
const EXAMPLE_OUTCOMES = [
  {
    quote: "Once I learned how to give AI better context, I stopped getting generic answers and started using it to write sharper sales messages.",
    label: "Example outcome: Sales & Marketing",
  },
  {
    quote: "I used to ask AI one-line questions. Now I use it to create checklists, summarize tasks, and build repeatable workflows.",
    label: "Example outcome: Automation",
  },
  {
    quote: "Better prompting helped me explain my app idea clearly enough to start building instead of staying stuck in my head.",
    label: "Example outcome: Build & Code",
  },
];

function ExampleOutcomesSection() {
  return (
    <section
      className="bg-[var(--surface)] border-y border-[var(--line)]"
      data-testid="landing-outcomes-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <motion.div {..._fade} className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05] font-semibold">
            Why People Train With AI
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--ink-2)] leading-relaxed">
            Realistic examples of what stronger AI skills can unlock.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {EXAMPLE_OUTCOMES.map((e, i) => (
            <motion.figure
              key={e.label}
              className="bg-white p-8 sm:p-10 rounded-[2rem]"
              style={{ border: "1px solid #E5E7EB", boxShadow: "0 1px 2px rgba(15,20,25,0.04)" }}
              {..._fade}
              transition={{ ..._fade.transition, delay: i * 0.08 }}
              data-testid={`landing-outcome-${i}`}
            >
              <figcaption className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--ink-3)] mb-6">
                {e.label}
              </figcaption>
              <blockquote className="font-display text-lg sm:text-xl text-[var(--ink)] leading-relaxed tracking-tight">
                &ldquo;{e.quote}&rdquo;
              </blockquote>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Dojo journey tile — unified clickable card for all 3 steps of the "Enter
 * the Dojo" section. Tile 1 starts the Skill Check (primary CTA); tiles 2
 * and 3 smooth-scroll to deeper sections on the same page so users can
 * preview what comes next without leaving the homepage.
 *
 * Visual hierarchy: the primary tile carries a saturated accent border +
 * solid action chip; secondary tiles use a soft tint accent + outline chip
 * so they don't compete for click attention with the Start Skill Check CTA.
 */
function DojoJourneyTile({ n, icon: Icon, title, desc, action, accent, primary = false, onClick, testid, delay = 0, track = "hero-cta" }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-track={track}
      className="relative rounded-[2rem] p-8 bg-white text-left group cursor-pointer flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
      style={{
        border: `1px solid ${primary ? accent : "#E5E7EB"}`,
        boxShadow: primary
          ? `0 8px 24px -12px ${accent}33`
          : "0 1px 2px rgba(15,20,25,0.04)",
      }}
      whileHover={{
        y: -4,
        boxShadow: `0 16px 36px -16px ${accent}44`,
        borderColor: accent,
        transition: { duration: 0.25 },
      }}
      whileTap={{ y: -1, transition: { duration: 0.1 } }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-6">
        {/* Round step badge */}
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center font-display font-semibold text-base"
          style={{ background: "var(--ink)", color: "#fff" }}
        >
          {n}
        </div>
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}12`, color: accent, border: `1px solid ${accent}22` }}
        >
          <Icon size={20} strokeWidth={2.25} />
        </div>
      </div>

      <h3 className="font-display text-xl leading-tight tracking-tight mb-2 text-[var(--ink)]">
        {title}
      </h3>
      <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-6">{desc}</p>

      {/* Action chip — solid for primary, outline for secondary */}
      <div
        className="mt-auto inline-flex self-start items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold"
        style={
          primary
            ? { background: accent, color: "#fff" }
            : { background: "#FAFAFA", color: "var(--ink)", border: "1px solid var(--line)" }
        }
      >
        {action}
        <span className="transition-transform group-hover:translate-x-0.5">→</span>
      </div>
    </motion.button>
  );
}
